import { QuestionStatus } from '@examdojo/models/question';
import { Constants } from '@examdojo/supabase';
// Recreate the types to avoid circular dependency.
const REFERENCE_QUESTION_STATUS = Constants.public.Enums.reference_question_status;
type ReferenceQuestionStatus = (typeof REFERENCE_QUESTION_STATUS)[number];

export interface Condition {
  id: ConditionId;
  expression: string;
}

export type ConditionReference = ConditionId;

export interface ChecklistConfig<TStatus = QuestionStatus | ReferenceQuestionStatus> {
  sections: Array<ChecklistSection<TStatus>>;
}

export interface ChecklistItem<TStatus = QuestionStatus | ReferenceQuestionStatus> {
  id: ChecklistItemId;
  display_name: string;
  type: ChecklistItemType;
  pass_condition: ConditionReference;
  in_statuses: TStatus[];
  rejection_statuses?: Array<{
    from: TStatus[];
    to: TStatus;
  }>;
  show_extra_text_field?: ConditionReference;
  explanation?: string;
  display?: ConditionReference;
  value?: boolean | string | number;
  referencedItemId?: ChecklistItemId;
  location?:
    | 'stimulus'
    | 'stem'
    | 'stem_group'
    | 'markscheme_criterion'
    | 'markscheme_solution'
    | 'options'
    | 'worked_solution';
}

export enum SectionType {
  Question = 'question',
  Markscheme = 'markscheme',
  Image = 'image',
  Table = 'table',
  MCQ = 'mcq',
  Other = 'other',
}

export interface ChecklistSection<TStatus = QuestionStatus | ReferenceQuestionStatus> {
  section_id: string | null;
  section_type: SectionType;
  section_index?: number;
  section_display_name?: string;
  checklist: Array<ChecklistItem<TStatus>>;
  rejection_statuses?: Array<{
    from: TStatus[];
    to: TStatus;
  }>;
}

export enum ChecklistItemType {
  YesNo = 'yes/no',
}

export interface FeedbackResponse {
  allChecklistItemsPassed: boolean;
  feedback: Feedback[];
}

export interface Feedback {
  section_id: string;
  section_type: SectionType;
  checklist: FeedbackItem[];
}

export interface FeedbackItem {
  id: ChecklistItemId;
  value: boolean | string;
  extra_text_field?: string;
  fixed?: boolean;
}

export enum ChecklistItemId {
  QuestionCorrectGeneral = 'question_correct_general',
  QuestionCorrectStructure = 'question_correct_structure',
  QuestionCorrectMath = 'question_correct_math',
  QuestionSolvableNoCalculator = 'question_solvable_no_calculator',

  QuestionImageCorrect = 'question_image_correct',

  QuestionFormatFormula = 'question_format_formula',
  QuestionFormatOther = 'question_format_other',

  MarkschemeCorrectGeneral = 'markscheme_correct_general',
  MarkschemeCorrectMath = 'markscheme_correct_math',
  MarkschemeCorrectMarks = 'markscheme_correct_marks',
  MarkschemesSolvableNoCalculator = 'markscheme_solvable_no_calculator',
  MarkschemeSolutionPaths = 'markscheme_solution_paths',

  MarkschemeImageCorrect = 'markscheme_image_correct',

  MarkschemeFormatFormula = 'markscheme_format_formula',
  MarkschemeFormatOther = 'markscheme_format_other',

  ImageCorrect = 'image_correct',

  Other = 'other',
}

export enum ConditionId {
  AlwaysVisible = 'always_visible',
  AnyResponse = 'any_response',
  NoResponse = 'no_response',
  ParentAnsweredNo = 'parent_answered_no',
  YesResponse = 'yes_response',
  HasCalculator = 'has_calculator',
  HasNoCalculator = 'has_no_calculator',
  BooleanValue = 'boolean_value',
  ReferencesChecklistItem = 'references_checklist_item',
  ReferencedItemIsTrue = 'referenced_item_is_true',
  HasMultipleSolutionPaths = 'has_multiple_solution_paths',
  InStem = 'in_stem',
}

export const CONDITIONS = [
  {
    id: ConditionId.AlwaysVisible,
    expression: 'true',
  },
  {
    id: ConditionId.AnyResponse,
    expression: '!!value',
  },
  {
    id: ConditionId.NoResponse,
    expression: 'value === false',
  },
  {
    id: ConditionId.YesResponse,
    expression: 'value === true',
  },
  {
    id: ConditionId.HasCalculator,
    expression: 'calculatorAllowed === true',
  },
  {
    id: ConditionId.HasNoCalculator,
    expression: 'calculatorAllowed === false',
  },
  {
    id: ConditionId.BooleanValue,
    expression: 'value === true',
  },
  {
    id: ConditionId.ReferencesChecklistItem,
    expression: 'getChecklistItemValue(referencedItemId) === true',
  },
  {
    id: ConditionId.ReferencedItemIsTrue,
    expression: 'getChecklistItemValue(referencedItemId) === true',
  },
  {
    id: ConditionId.HasMultipleSolutionPaths,
    expression: 'totalSolutionPaths > 1',
  },
  {
    id: ConditionId.InStem,
    expression: 'location === "stem"',
  },
];
